import type {CompRef, GridItemLayout, Margins, SingleLayoutData} from '@wix/document-services-types'
import {COMP_TYPES, DATA_TYPES} from '../../../constants/constants'
import type {ExtensionAPI} from '@wix/document-manager-core'
import type {StructureExtensionAPI} from '../../structure'
import type {VariantsExtensionAPI} from '../../variants/variants'
import type {DataModelAPI} from '../../dataModel/dataModel'
import type {ManifestApi} from '../../builderComponents/manifest'
import type {ConverterArguments, ConvertWidthToPx} from './types'

export const getLayout = (extensionAPI: ExtensionAPI, compRef: CompRef): SingleLayoutData => {
    const {variants} = extensionAPI as VariantsExtensionAPI
    return variants.getItemConsideringVariantsForPointer(compRef, DATA_TYPES.layout)
}

const sectionTypes = [COMP_TYPES.RESPONSIVE_SECTION, COMP_TYPES.HEADER_SECTION, COMP_TYPES.FOOTER_SECTION]
export const isSection = (extensionAPI: ExtensionAPI, compRef: CompRef): boolean => {
    const {components} = extensionAPI as StructureExtensionAPI
    return sectionTypes.includes(components.getComponentType(compRef))
}

const pageTypes = [COMP_TYPES.PAGE_TYPE]
export const isPage = (extensionAPI: ExtensionAPI, compRef: CompRef): boolean => {
    const {components} = extensionAPI as StructureExtensionAPI
    return pageTypes.includes(components.getComponentType(compRef))
}

export const clampMinMax = (value: number, min?: number, max?: number): number => {
    let res = value
    if (typeof min === 'number') res = Math.max(min, res)
    if (typeof max === 'number') res = Math.min(max, res)
    return res
}

export const getStaleMeasurements = (dataModel: DataModelAPI, compRef: CompRef) => {
    return dataModel.components.getItem(compRef, DATA_TYPES.measurements)
}

export const getManifestInitialSize = (manifest: ManifestApi, compRef: CompRef) => {
    return manifest.layout.getInitialSize(compRef)
}

export const getValue = (object: any) => ('value' in object ? object.value : undefined)

export const getMin = (object: any) => ('min' in object ? object.min : undefined)

export const getMax = (object: any) => ('max' in object ? object.max : undefined)

export function getParentWidth(converterArguments: ConverterArguments) {
    const {measurementsMapAPI, calculate, pointers, compRef} = converterArguments
    const {get} = measurementsMapAPI

    calculate.parent.width(converterArguments)
    calculate.parent.columns(converterArguments)

    const parentRef = pointers.structure.getParent(compRef) as CompRef
    return getColumnsWidthFromGridArea(converterArguments) ?? get.width(parentRef)
}

export function getParentHeight(converterArguments: ConverterArguments) {
    const {measurementsMapAPI, calculate, pointers, compRef} = converterArguments
    const {get} = measurementsMapAPI

    calculate.parent.height(converterArguments)
    calculate.parent.rows(converterArguments)

    const parentRef = pointers.structure.getParent(compRef) as CompRef
    return getRowsHeightFromGridArea(converterArguments) ?? get.height(parentRef)
}

function getColumnsWidthFromGridArea(converterArguments: ConverterArguments) {
    const gridArea = (converterArguments.layoutData?.itemLayout as GridItemLayout)?.gridArea
    if (!gridArea) return undefined

    return getColumnsWidth(converterArguments, gridArea.columnStart - 1, gridArea.columnEnd - 1)
}

export function getLeftColumnsWidth(converterArguments: ConverterArguments): number {
    const {gridArea} = converterArguments.layoutData.itemLayout as GridItemLayout
    if (!gridArea) return 0

    return getColumnsWidth(converterArguments, 0, gridArea.columnStart - 1)
}

function getColumnsWidth(converterArguments: ConverterArguments, columnStart: number, columnEnd: number) {
    const {measurementsMapAPI, calculate, pointers, compRef} = converterArguments
    const {get} = measurementsMapAPI

    calculate.parent.columns(converterArguments)
    const parentRef = pointers.structure.getParent(compRef) as CompRef
    const columns = get.columns(parentRef)

    let columnsWidth = 0
    for (let i = columnStart; i < columnEnd; i++) {
        columnsWidth += columns[i]
    }
    return columnsWidth
}

function getRowsHeightFromGridArea(converterArguments: ConverterArguments) {
    const gridArea = (converterArguments.layoutData?.itemLayout as GridItemLayout)?.gridArea
    if (!gridArea) return undefined

    return getRowsHeight(converterArguments, gridArea.rowStart - 1, gridArea.rowEnd - 1)
}

export function getTopRowsHeight(converterArguments: ConverterArguments): number {
    const {gridArea} = converterArguments.layoutData.itemLayout as GridItemLayout
    if (!gridArea) return 0

    return getRowsHeight(converterArguments, 0, gridArea.rowStart - 1)
}

function getRowsHeight(converterArguments: ConverterArguments, rowStart: number, rowEnd: number) {
    const {measurementsMapAPI, calculate, pointers, compRef} = converterArguments
    const {get} = measurementsMapAPI

    calculate.parent.rows(converterArguments)
    const parentRef = pointers.structure.getParent(compRef) as CompRef
    const rows = get.rows(parentRef)

    let rowsHeight = 0
    for (let i = rowStart; i < rowEnd; i++) {
        rowsHeight += rows[i]
    }
    return rowsHeight
}

function getMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins: Margins | undefined,
    side: keyof Margins
): number {
    return margins?.[side]?.type
        ? convertWidthToPx[margins[side]!.type]({
              ...converterArguments,
              value: getValue(margins[side])
          })
        : 0
}

// in CSS top and bottom margins are calculated based on width
export function getTopMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins?: Margins
) {
    return getMarginInPx(converterArguments, convertWidthToPx, margins, 'top')
}
export function getBottomMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins?: Margins
) {
    return getMarginInPx(converterArguments, convertWidthToPx, margins, 'bottom')
}
export function getLeftMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins?: Margins
) {
    return getMarginInPx(converterArguments, convertWidthToPx, margins, 'left')
}
export function getRightMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins?: Margins
) {
    return getMarginInPx(converterArguments, convertWidthToPx, margins, 'right')
}
